import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Star, Calendar, Users, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Layout } from '../components/layout/Layout';
import { ImageGallery } from '../components/tour-detail/ImageGallery';
import { PackageInfo } from '../components/tour-detail/PackageInfo';
import { TabSection } from '../components/tour-detail/TabSection';
import { RelatedPackages } from '../components/tour-detail/RelatedPackages';
import { Breadcrumbs } from '../components/tour-detail/Breadcrumbs';
import { EnquiryModal } from '../components/tours/EnquiryModal';
import { publicApi } from '../lib/api';
import { formatPrice, categoryColors } from '../lib/utils';
import { 
  SEO, 
  TourPackageSchema, 
  TourServiceSchema, 
  BreadcrumbSchema,
  siteConfig 
} from '../components/seo';

export default function TourDetailPage() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const loadTour = useCallback(async () => {
    try {
      setLoading(true);
      const response = await publicApi.getTourBySlug(slug);
      setTour(response.data);
    } catch (error) {
      console.error('Error loading tour:', error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadTour();
    // Scroll to top when tour changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loadTour]);

  // Loading State
  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Breadcrumb Skeleton */}
          <Skeleton className="h-5 w-64 mb-6" />
          
          {/* Gallery Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">
            <div className="lg:col-span-3">
              <Skeleton className="h-[400px] lg:h-[500px] w-full rounded-2xl" />
            </div>
            <div className="flex lg:flex-col gap-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="w-24 h-24 lg:w-full lg:h-28 rounded-xl" />
              ))}
            </div>
          </div>

          {/* Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div>
                <Skeleton className="h-6 w-24 mb-3" />
                <Skeleton className="h-10 w-3/4 mb-4" />
                <Skeleton className="h-5 w-1/2" />
              </div>
              <Skeleton className="h-12 w-full rounded-xl" />
              <div className="space-y-4">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
            <div>
              <Skeleton className="h-[400px] w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Not Found State
  if (!tour) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <MapPin className="w-12 h-12 text-[#4A4A4A]" />
          </div>
          <h1 
            className="text-3xl font-bold mb-4 text-[#1A1A1A]"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Tour Not Found
          </h1>
          <p className="text-[#4A4A4A] mb-8 max-w-md mx-auto">
            Sorry, we couldn&apos;t find the tour you&apos;re looking for. It may have been removed or the link is incorrect.
          </p>
          <Link to="/tours">
            <Button className="bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full px-8">
              Browse All Tours
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const discountPercent = tour.discounted_price
    ? Math.round(((tour.price - tour.discounted_price) / tour.price) * 100)
    : 0;

  return (
    <Layout>
      {/* SEO Meta Tags */}
      <SEO 
        title={tour.name}
        description={tour.description?.substring(0, 160) || `Explore ${tour.name} with NYRAV Tours. ${tour.duration_days} days of unforgettable experiences.`}
        keywords={`${tour.name}, ${tour.destination}, ${tour.category} tour, travel package, india tour`}
        image={tour.images?.[0]}
        url={`${siteConfig.siteUrl}/tours/${tour.slug}`}
        type="product"
        product={{
          price: tour.discounted_price || tour.price,
          available: tour.status === 'active'
        }}
      />
      
      {/* JSON-LD Structured Data */}
      <TourPackageSchema tour={tour} />
      <TourServiceSchema tour={tour} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Tours', url: '/tours' },
        { name: tour.category, url: `/tours?category=${tour.category}` },
        { name: tour.name }
      ]} />

      {/* Breadcrumbs */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Breadcrumbs
            items={[
              { label: 'Tours', href: '/tours' },
              { label: tour.category, href: `/tours?category=${tour.category}` },
              { label: tour.name }
            ]}
          />
        </div>
      </div>

      {/* Image Gallery Section */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <ImageGallery images={tour.images} title={tour.name} />
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2">
            {/* Tour Header */}
            <div className="mb-8" data-testid="tour-header">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Badge className={categoryColors[tour.category] || 'bg-gray-100'}>
                  {tour.category}
                </Badge>
                {tour.featured && (
                  <Badge className="bg-[#FF6B35] text-white">
                    🔥 Best Seller
                  </Badge>
                )}
                {discountPercent > 0 && (
                  <Badge className="bg-[#2A9D8F] text-white">
                    {discountPercent}% OFF
                  </Badge>
                )}
                <div className="flex items-center gap-1 text-sm">
                  <Star className="w-4 h-4 fill-[#E9C46A] text-[#E9C46A]" />
                  <span className="font-medium">4.8</span>
                  <span className="text-[#4A4A4A]">(120 reviews)</span>
                </div>
              </div>

              {/* Title */}
              <h1 
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="tour-title"
              >
                {tour.name}
              </h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 md:gap-6 text-[#4A4A4A]">
                <span className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#FF6B35]" />
                  <span className="font-medium">{tour.destination}</span>
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#FF6B35]" />
                  <span>{tour.duration_days} Days / {tour.duration_nights} Nights</span>
                </span>
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#FF6B35]" />
                  <span>Max {tour.max_group_size || 20} People</span>
                </span>
              </div>

              {/* Mobile Price (shown only on mobile) */}
              <div className="mt-6 p-4 bg-gradient-to-r from-[#004E64] to-[#006B87] rounded-xl lg:hidden">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white/80 text-sm">Starting from</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-white">
                        {formatPrice(tour.discounted_price || tour.price)}
                      </span>
                      {tour.discounted_price && (
                        <span className="text-sm text-white/60 line-through">
                          {formatPrice(tour.price)}
                        </span>
                      )}
                    </div>
                    <span className="text-white/80 text-xs">per person</span>
                  </div>
                  <Button 
                    onClick={() => setEnquiryOpen(true)}
                    className="bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full"
                  >
                    Enquire Now
                  </Button>
                </div>
              </div>
            </div>

            {/* Tab Section */}
            <TabSection tour={tour} />
          </div>

          {/* Right Column - Booking Sidebar */}
          <div className="hidden lg:block">
            <PackageInfo tour={tour} onEnquireClick={() => setEnquiryOpen(true)} />
          </div>
        </div>
      </section>

      {/* Related Packages */}
      <RelatedPackages 
        currentTourId={tour.id} 
        category={tour.category}
        destination={tour.destination}
      />

      {/* Enquiry Modal */}
      <EnquiryModal
        isOpen={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        tour={tour}
      />
    </Layout>
  );
}
